/**
 * Logship pipeline health, from POST /qa_scrubber/logship-status.
 *
 * Four stages per company - ETL, Normalized, Merged and ioIQ. Each cell shows the last file that
 * reached that stage, coloured by when our pipeline processed it: green under 2h, amber under 4h,
 * red beyond.
 *
 * Why 2h rather than 1h: the vendor sends the whole batch hourly at :15 and the lambda picks
 * files up at :00 and :30, so a healthy last file is routinely about an hour old just before the
 * next one is picked up. A 1h window would turn every company amber for part of every hour.
 *
 * The company status is the WORST of its stages, not the freshest: a file processed minutes ago
 * must not hide a stage that stopped hours ago.
 */

export const LOGSHIP_HEALTHY_WINDOW_MS = 2 * 60 * 60 * 1000; // 2 hours
export const LOGSHIP_WARNING_WINDOW_MS = 4 * 60 * 60 * 1000; // 4 hours

/** `none` = nothing to judge: no data for the stage, or a file type the company doesn't get. */
export type LogshipHealth = 'healthy' | 'warning' | 'stale' | 'none';
export type LogshipStageKey = 'etl' | 'normalized' | 'merged' | 'ioiq';
export type LogshipFileStatus =
  | 'loaded'
  | 'skipped'
  | 'failed'
  | 'partial'
  | 'normalized'
  | 'merged'
  | 'scored';
export type LogshipTypeExpectation = 'expected' | 'not_expected' | 'retired';

/* ---------- API shape: timestamps are ISO strings on the wire ---------- */

export interface ApiLogshipFile {
  fileType: string | null;
  fileName: string | null;
  /** First timestamp in the file name - when the vendor wrote it. */
  arrivedAt: string | null;
  /** When the stage handled it: ETL pick-up, the normalizing run, or the merge. */
  processedAt: string | null;
  status: LogshipFileStatus;
}

export interface ApiLogshipTypeRow {
  fileType: string;
  family: 'IOExtract' | 'OasisAnswers';
  expectation: LogshipTypeExpectation;
  file: ApiLogshipFile | null;
  note?: string | null;
}

export interface ApiLogshipStage {
  available: boolean;
  latest: ApiLogshipFile | null;
  failingSince: string | null;
  types: ApiLogshipTypeRow[];
  note?: string | null;
}

export interface ApiCompanyLogshipStatus {
  companyId: string;
  company: string;
  expectedForms: string[];
  stages: Record<LogshipStageKey, ApiLogshipStage>;
}

export interface LogshipStatusResponse {
  success: boolean;
  data?: ApiCompanyLogshipStatus[] | null;
  /** Set by the backend when success is false. */
  message?: string | null;
}

/* ---------- view model ---------- */

export interface LogshipFile {
  fileType: string | null;
  fileName: string | null;
  arrivedAt: Date | null;
  processedAt: Date | null;
  status: LogshipFileStatus;
}

export interface LogshipTypeRow {
  fileType: string;
  label: string;
  expectation: LogshipTypeExpectation;
  file: LogshipFile | null;
  note: string | null;
  health: LogshipHealth;
}

export interface LogshipStageCell {
  key: LogshipStageKey;
  label: string;
  available: boolean;
  latest: LogshipFile | null;
  health: LogshipHealth;
  failingSince: Date | null;
  types: LogshipTypeRow[];
  note: string | null;
}

export interface LogshipCompanyRow {
  companyId: string;
  company: string;
  cells: LogshipStageCell[];
  overall: LogshipHealth;
}

/** Column order - the order data actually flows through the pipeline. */
export const LOGSHIP_STAGE_ORDER: LogshipStageKey[] = ['etl', 'normalized', 'merged', 'ioiq'];

export const LOGSHIP_STAGE_LABELS: Record<LogshipStageKey, string> = {
  etl: 'ETL',
  normalized: 'Normalized',
  merged: 'Merged',
  ioiq: 'ioIQ',
};

export function parseTimestamp(value: unknown): Date | null {
  if (!value) return null;
  const date = new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function healthForTimestamp(timestamp: Date | null, now: Date): LogshipHealth {
  if (!timestamp) return 'none';
  const age = now.getTime() - timestamp.getTime();
  // A timestamp slightly in the future is still fresh - clock skew must not read as stale.
  if (age < LOGSHIP_HEALTHY_WINDOW_MS) return 'healthy';
  if (age < LOGSHIP_WARNING_WINDOW_MS) return 'warning';
  return 'stale';
}

const SEVERITY: Record<LogshipHealth, number> = { none: -1, healthy: 0, warning: 1, stale: 2 };

/**
 * ioIQ is shown for information, not judged. io IQ v4.1 scores as the grid is opened rather than
 * on a schedule, so the age of its last run says nothing about the pipeline: it carries no colour
 * of its own and never decides a company's status (agreed 21 Sep).
 */
export const LOGSHIP_INFORMATIONAL_STAGES: readonly LogshipStageKey[] = ['ioiq'];

export function isInformationalStage(key: LogshipStageKey): boolean {
  return LOGSHIP_INFORMATIONAL_STAGES.includes(key);
}

/** Worst health among the cells that have something to judge; nothing at all is itself stale. */
export function rollUpHealth(healths: LogshipHealth[]): LogshipHealth {
  const judged = healths.filter((health) => health !== 'none');
  if (!judged.length) return 'stale';
  return judged.reduce((worst, health) => (SEVERITY[health] > SEVERITY[worst] ? health : worst), 'healthy');
}

/**
 * What a file is judged by: when our pipeline processed it, the same as a merge or an ioIQ run.
 * The time in its file name is only the fallback - a back-filled file named weeks ago but processed
 * minutes ago is fresh.
 */
export function fileTime(file: LogshipFile | null): Date | null {
  return file ? file.processedAt ?? file.arrivedAt : null;
}

/** `IOExtract_CSV3_VisitNotes` -> `CSV3 VisitNotes`; `OasisAnswers_HOPE_Post` -> `HOPE Post`. */
export function fileTypeLabel(fileType: string | null): string {
  if (!fileType) return '';
  for (const family of ['IOExtract_', 'OasisAnswers_']) {
    if (fileType.startsWith(family)) return fileType.slice(family.length).replace('_', ' ');
  }
  return fileType;
}

function toFile(api: ApiLogshipFile | null | undefined): LogshipFile | null {
  if (!api) return null;
  return {
    fileType: api.fileType ?? null,
    fileName: api.fileName ?? null,
    arrivedAt: parseTimestamp(api.arrivedAt),
    processedAt: parseTimestamp(api.processedAt),
    status: api.status,
  };
}

function toTypeRow(api: ApiLogshipTypeRow, now: Date): LogshipTypeRow {
  const file = toFile(api.file);
  const note = api.note || null;
  let health: LogshipHealth = 'none';
  if (api.expectation === 'expected' && !note) {
    // An expected type with nothing in the history window has stopped arriving.
    health = file ? healthForTimestamp(fileTime(file), now) : 'stale';
  }
  return {
    fileType: api.fileType,
    label: fileTypeLabel(api.fileType),
    expectation: api.expectation,
    file,
    note,
    health,
  };
}

/** Inside More, problems first: missing or late expected types, then the rest in catalogue order. */
function rowRank(row: LogshipTypeRow): number {
  if (row.expectation === 'retired') return 5;
  if (row.expectation === 'not_expected') return 4;
  if (row.health === 'none') return 3;
  return 2 - SEVERITY[row.health];
}

function toCell(key: LogshipStageKey, api: ApiLogshipStage | null | undefined, now: Date): LogshipStageCell {
  const available = api?.available ?? false;
  const latest = toFile(api?.latest);
  const types = (api?.types ?? [])
    .map((row, index) => ({ row: toTypeRow(row, now), index }))
    .sort((a, b) => rowRank(a.row) - rowRank(b.row) || a.index - b.index)
    .map(({ row }) => row);
  return {
    key,
    label: LOGSHIP_STAGE_LABELS[key],
    available,
    latest,
    health: available && !isInformationalStage(key) ? healthForTimestamp(fileTime(latest), now) : 'none',
    failingSince: parseTimestamp(api?.failingSince),
    types,
    note: api?.note || null,
  };
}

export function buildCompanyRow(api: ApiCompanyLogshipStatus, now: Date): LogshipCompanyRow {
  const cells = LOGSHIP_STAGE_ORDER.map((key) => toCell(key, api.stages?.[key], now));
  return {
    companyId: api.companyId,
    company: api.company,
    cells,
    overall: rollUpHealth(
      cells.filter((cell) => cell.available && !isInformationalStage(cell.key)).map((cell) => cell.health),
    ),
  };
}

/** Worst-first, then alphabetical - the companies needing attention come to the top. */
export function mapLogshipStatusResponse(
  data: ApiCompanyLogshipStatus[] | null | undefined,
  now: Date,
): LogshipCompanyRow[] {
  if (!data) return [];
  return data
    .map((company) => buildCompanyRow(company, now))
    .sort((a, b) => SEVERITY[b.overall] - SEVERITY[a.overall] || a.company.localeCompare(b.company));
}

/** Compact relative age for display, e.g. "12m ago" / "3h ago" / "2d ago". */
export function formatAge(timestamp: Date | null, now: Date): string {
  if (!timestamp) return 'never';
  const ms = now.getTime() - timestamp.getTime();
  if (ms < 0) return 'just now';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
